import React, { useEffect, useState } from 'react';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Alert, Button, Tooltip } from 'antd';
// Api
import { SET_CAMPAIGN_AGREEMENT_SIGNATORY } from 'api/campaignV2/mutations';
// Types
import {
  GetCampaignsV2,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  SetCampaignAgreementSignatory,
  SetCampaignAgreementSignatoryVariables,
} from 'api/campaignV2/types/SetCampaignAgreementSignatory';
import { CampaignStatus } from 'api/graphql-global-types';
// Ui
import InfoTip from 'ui/InfoTip/InfoTip';
import { successNotification, errorNotification } from 'ui/Notification';
// Components
import AgreementSignatorySelect, {
  getSignatoryName,
  SIGNATORY_TIP,
} from 'components/common/AgreementSignatorySelect/AgreementSignatorySelect';
// Styles
import styles from './ChangeAgreementSignatory.module.scss';

type ChangeAgreementSignatoryProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  refetch: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
};

/** Same as the backend: the signatory can change until the campaign is approved */
export const SIGNATORY_EDITABLE_STATUSES = [
  CampaignStatus.DETAIL_FILLED,
  CampaignStatus.ON_REVIEW,
];

const LOCKED_TIP =
  'The signatory can only be changed before the campaign is approved. Talent agreements are already issued in their name.';

const ChangeAgreementSignatory = ({
  record,
  refetch,
}: ChangeAgreementSignatoryProps): JSX.Element => {
  const currentId = record.agreementSignatoryId ?? null;
  const [signatoryId, setSignatoryId] = useState<string | null>(currentId);

  useEffect(() => {
    setSignatoryId(currentId);
  }, [currentId]);

  const [setSignatory, { loading }] = useMutation<
    SetCampaignAgreementSignatory,
    SetCampaignAgreementSignatoryVariables
  >(SET_CAMPAIGN_AGREEMENT_SIGNATORY);

  const isEditable = SIGNATORY_EDITABLE_STATUSES.includes(record.status);
  const hasChanged = !!signatoryId && signatoryId !== currentId;

  const handleSave = async () => {
    if (!signatoryId) {
      return;
    }

    try {
      await setSignatory({
        variables: {
          input: { campaignId: record.id, agreementSignatoryId: signatoryId },
        },
      });
      successNotification('Agreement signatory has been updated');
      refetch();
    } catch (error: any) {
      console.error('Error updating agreement signatory', error);
      errorNotification(error?.message || 'Error updating agreement signatory');
    }
  };

  if (!isEditable) {
    return (
      <div className={styles.root}>
        <p className={styles.label}>
          Agreement signatory
          <InfoTip title={SIGNATORY_TIP} />
        </p>
        <Tooltip title={LOCKED_TIP}>
          <span className={styles.value}>
            {record.agreementSignatory
              ? `${getSignatoryName(record.agreementSignatory)} (${
                  record.agreementSignatory.email
                })`
              : 'Not set'}
          </span>
        </Tooltip>
        <p className={styles.explanation}>{LOCKED_TIP}</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <p className={styles.label}>
        Agreement signatory
        <InfoTip title={SIGNATORY_TIP} />
      </p>

      {!currentId && (
        <Alert
          className={styles.alert}
          type="warning"
          showIcon
          message="No signatory yet"
          description="This campaign requires the digital agreement, so it can't be approved until a signatory is chosen."
        />
      )}

      <div className={styles.inputContainer}>
        <div className={styles.selectWrapper}>
          <AgreementSignatorySelect
            campaignId={record.id}
            ownerId={record.agency?.id}
            value={signatoryId}
            onChange={setSignatoryId}
          />
        </div>

        <Tooltip
          title={hasChanged ? undefined : 'Choose a different signatory first'}
        >
          <Button
            type="primary"
            onClick={handleSave}
            loading={loading}
            disabled={!hasChanged}
          >
            Update signatory
          </Button>
        </Tooltip>
      </div>

      <p className={styles.explanation}>
        Can only be changed before the campaign is approved. A brand user
        launching the campaign becomes the signatory.
      </p>
    </div>
  );
};

export default ChangeAgreementSignatory;
